export function diff<T extends object>(a: T, b: T): Diff<T> {
  const result: Record<string, unknown> = {};

  const aKeys = Object.keys(a) as (keyof T & string)[];
  const bKeys = Object.keys(b) as (keyof T & string)[];

  // Check keys in a
  for (const key of aKeys) {
    const aVal = a[key];
    const bVal = b[key];

    if (!(key in b)) {
      // Key removed
      result[`-${key}`] = aVal;
    } else if (isObject(aVal) && isObject(bVal)) {
      // Both are objects - recurse
      const nested = diff(aVal, bVal);
      if (nested !== undefined) {
        result[key] = nested;
      }
    } else if (aVal !== bVal) {
      // Value changed
      result[`-${key}`] = aVal;
      result[`+${key}`] = bVal;
    }
  }

  // Check keys only in b (added)
  for (const key of bKeys) {
    if (!(key in a)) {
      result[`+${key}`] = b[key];
    }
  }

  // Return undefined if no differences
  return Object.keys(result).length === 0 ? undefined : (result as Diff<T> & object);
}

function isObject(val: unknown): val is object {
  return val !== null && typeof val === "object";
}

// diff({a: 1, b: 2}, {a: 2, c: 2}) => {'-a': 1, '+a': 2, '-b': 2, '+c': 2}
// diff({a: {x: 1}}, {a: {x: 2}}) => {a: {'-x': 1, '+x': 2}}

type OptionalKeys<T> = {
  [K in keyof T]-?: {} extends Pick<T, K> ? K : never;
}[keyof T];

// Keys that can have +/- in diff:
// - Optional keys (can be added/removed)
// - Required keys with non-object values (primitives can change)
type DiffableKeys<T> = {
  [K in keyof T]: K extends OptionalKeys<T> ? K : T[K] extends object ? never : K;
}[keyof T];

type Diff<T extends object> =
  | ({
      [K in keyof T]?: Diff<T[K] & object>;
    } & {
      [K in DiffableKeys<T> as `+${K & string}`]?: T[K];
    } & {
      [K in DiffableKeys<T> as `-${K & string}`]?: T[K];
    })
  | undefined;
